/* Header: search overlay with suggestions, menu/account drawers, desktop sub-menu panels, wishlist count;
   closes its panels when the cart drawer opens. */
(() => {
  if (customElements.get('nt-header')) return;
  const esc = (s) => String(s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));

  class NtHeader extends HTMLElement {
    connectedCallback() {
      this.fullSearch = this.dataset.search === 'full';
      this.addEventListener('click', (e) => {
        const open = e.target.closest('[data-nt-open]');
        if (open) { e.preventDefault(); this.toggle(open.dataset.ntOpen, open); return; }
        if (e.target.closest('[data-nt-close]')) { e.preventDefault(); this.closeAll(); return; }
        /* Predictive results: the "Search for …" row submits the search form. */
        const term = e.target.closest('[data-nt-results] .predictive-search__item--term, [data-nt-results] #predictive-search-option-search-keywords button');
        if (term) { e.preventDefault(); const f = this.querySelector('#NtSearch form'); if (f) f.submit(); return; }
        /* Cart icon: Dawn's cart drawer / notification opens itself from #cart-icon-bubble; only close our own panels first. */
        if (e.target.closest('[data-nt-cart]')) { this.closeAll(true); this.measure(); }
      });
      this.onKey = (e) => {
        if (e.key !== 'Escape') return;
        if (this.current) this.closeAll();
        this.closePanels();
      };
      document.addEventListener('keydown', this.onKey);
      /* Measure in an animation frame (after the browser's own layout), never during script start-up. */
      const schedule = () => { if (!this.ticking) { this.ticking = true; requestAnimationFrame(() => { this.ticking = false; this.measure(); }); } };
      schedule();
      addEventListener('resize', schedule, { passive: true });
      addEventListener('scroll', schedule, { passive: true });
      const input = this.querySelector('[data-nt-suggest]');
      if (input) {
        let t;
        input.addEventListener('input', () => { clearTimeout(t); t = setTimeout(() => this.suggest(input.value.trim()), this.fullSearch ? 200 : 180); });
      }
      if (this.fullSearch) {
        const search = this.querySelector('#NtSearch');
        if (search) search.addEventListener('click', (e) => { if (e.target === search) this.closeAll(); });
      }
      this.initPanels();
      this.initWishlist();
    }
    disconnectedCallback() { document.removeEventListener('keydown', this.onKey); }

    /* Where the header ends on screen: panels start their content below it. */
    /* Writes only changed values: a custom property set on <html> restyles the whole page. */
    measure() {
      const root = document.documentElement.style;
      const set = (name, value) => { if (root.getPropertyValue(name) !== value) root.setProperty(name, value); };
      const bar = this.querySelector('.nt-hd__bar');
      if (bar) set('--nt-hd-bottom', `${Math.max(0, Math.round(bar.getBoundingClientRect().bottom))}px`);
      /* Full header height at the top of the page (announcement + header), used by the hero's 'share of screen' height. */
      let h = 0; document.querySelectorAll('.shopify-section-group-header-group').forEach((el) => { h += el.offsetHeight; });
      if (h) set('--nt-header-h', `${h}px`);
    }
    closeCart() {
      const drawer = document.querySelector('cart-drawer');
      if (drawer && drawer.classList.contains('active') && typeof drawer.close === 'function') drawer.close();
    }
    panel(name) { return this.querySelector({ search: '#NtSearch', menu: '#NtMenu', account: '#NtAccount' }[name]); }
    /* Full-screen search and the narrow drawer cover the whole page, announcement bar included. */
    covers(name) { return (name === 'search' && this.fullSearch) || (name === 'menu' && this.classList.contains('nt-hd--drawer-narrow')); }
    toggle(name, trigger) {
      if (this.current === name) { this.closeAll(); return; }
      this.closeAll(true);
      this.closeCart();
      this.closePanels();
      this.measure();
      const el = this.panel(name); if (!el) return;
      this.current = name; this.trigger = trigger;
      el.hidden = false;
      requestAnimationFrame(() => el.classList.add('is-open'));
      this.querySelectorAll(`[data-nt-open="${name}"]`).forEach((b) => b.setAttribute('aria-expanded', 'true'));
      if (name !== 'search' || this.fullSearch) document.body.classList.add('nt-lock');
      if (this.covers(name)) document.documentElement.classList.add('nt-hd-cover');
      if (name === 'search' && this.fullSearch) this.status('start');
      const focus = el.querySelector('input, a, button:not([data-nt-close])') || el.querySelector('button');
      setTimeout(() => focus && focus.focus(), 60);
    }
    closeAll(silent) {
      if (!this.current) return;
      const name = this.current;
      const el = this.panel(name);
      el.classList.remove('is-open');
      const delay = name === 'search' ? 0 : 450;
      setTimeout(() => { if (!el.classList.contains('is-open')) el.hidden = true; }, delay);
      document.body.classList.remove('nt-lock');
      document.documentElement.classList.remove('nt-hd-cover');
      this.querySelectorAll(`[data-nt-open="${name}"]`).forEach((b) => b.setAttribute('aria-expanded', 'false'));
      if (name === 'search' && this.fullSearch) {
        if (this.ctrl) this.ctrl.abort();
        const input = this.querySelector('[data-nt-suggest]'); if (input) input.value = '';
        const box = this.querySelector('[data-nt-results]'); if (box) box.innerHTML = '';
        this.status('');
      }
      if (this.trigger && !silent && this.trigger.offsetParent !== null) this.trigger.focus();
      this.current = null;
    }
    status(kind) {
      const el = this.querySelector('[data-nt-status]');
      if (!el) return;
      const text = kind ? (el.dataset[`text${kind[0].toUpperCase()}${kind.slice(1)}`] || '') : '';
      el.textContent = text;
      el.hidden = !text;
    }
    async suggest(q) {
      const box = this.querySelector('[data-nt-results]');
      if (!box) return;
      const min = Number(this.dataset.minChars || 2);
      if (!q || q.length < min) { box.innerHTML = ''; if (this.fullSearch) this.status(q ? '' : 'start'); return; }
      const root = window.Shopify?.routes?.root || '/';
      if (this.fullSearch) {
        /* Full screen: the theme's predictive search section (products, collections, articles) with store-formatted prices. */
        if (this.ctrl) this.ctrl.abort();
        this.ctrl = new AbortController();
        const id = (this.reqId = (this.reqId || 0) + 1);
        this.status('loading');
        try {
          const url = `${(window.routes && window.routes.predictive_search_url) || `${root}search/suggest`}?q=${encodeURIComponent(q)}&resources[type]=product,collection,article&resources[limit]=6&section_id=predictive-search`;
          const res = await fetch(url, { signal: this.ctrl.signal });
          const html = await res.text();
          if (id !== this.reqId) return;
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const results = doc.querySelector('#predictive-search-results') || doc.querySelector('.shopify-section') || doc.body;
          box.innerHTML = results ? results.innerHTML : '';
          box.querySelectorAll('[tabindex="-1"]').forEach((a) => a.removeAttribute('tabindex'));
          box.querySelectorAll('[id]').forEach((n) => n.removeAttribute('id'));
          this.status(box.querySelector('.predictive-search__list-item a, .predictive-search__item[href]') ? '' : 'none');
        } catch (e) {
          if (e && e.name === 'AbortError') return;
          box.innerHTML = '';
          this.status('none');
        }
        return;
      }
      try {
        const url = `${root}search/suggest.json?q=${encodeURIComponent(q)}&resources[type]=product&resources[limit]=5`;
        const data = await (await fetch(url)).json();
        const items = data.resources?.results?.products || [];
        box.innerHTML = items.map((p) => `<a class="nt-hd__res" href="${esc(p.url)}">${p.image ? `<img src="${esc(p.image)}&width=88" alt="" width="44" height="44" loading="lazy">` : ''}<span>${esc(p.title)}</span><span>${esc(p.price ? '£' + p.price : '')}</span></a>`).join('')
          + `<a class="nt-hd__res nt-hd__res--all" href="/search?q=${encodeURIComponent(q)}">See all results for “${esc(q)}”</a>`;
      } catch (e) { box.innerHTML = ''; }
    }

    /* Desktop sub-menu panels: open on hover / focus, first tap opens on touch screens, Esc or outside click closes. */
    initPanels() {
      const nav = this.querySelector('[data-nt-panels]');
      if (!nav) return;
      this.items = Array.from(nav.querySelectorAll('[data-nt-item]'));
      this.items.forEach((item) => {
        const panel = item.querySelector('[data-nt-panel]');
        const trigger = item.querySelector('[data-nt-trigger]');
        if (!panel || !trigger) return;
        let timer = null;
        const open = () => { clearTimeout(timer); this.closePanels(item); panel.hidden = false; trigger.setAttribute('aria-expanded', 'true'); };
        const later = () => { clearTimeout(timer); timer = setTimeout(() => { panel.hidden = true; trigger.setAttribute('aria-expanded', 'false'); }, 160); };
        item.addEventListener('mouseenter', open);
        item.addEventListener('mouseleave', later);
        trigger.addEventListener('focus', open);
        item.addEventListener('focusout', (e) => { if (!item.contains(e.relatedTarget)) later(); });
        trigger.addEventListener('click', (e) => {
          if (window.matchMedia('(hover: none)').matches && panel.hidden) { e.preventDefault(); open(); }
        });
      });
      document.addEventListener('click', (e) => { if (!nav.contains(e.target)) this.closePanels(); });
    }
    closePanels(except) {
      (this.items || []).forEach((item) => {
        if (item === except) return;
        const panel = item.querySelector('[data-nt-panel]');
        const trigger = item.querySelector('[data-nt-trigger]');
        if (panel) panel.hidden = true;
        if (trigger) trigger.setAttribute('aria-expanded', 'false');
      });
    }

    /* Wishlist count: products saved with the card hearts (localStorage "nt-wishlist", event "nt:wishlist:change"). */
    initWishlist() {
      const counts = this.querySelectorAll('[data-nt-wishlist-count]');
      if (!counts.length) return;
      const read = () => {
        try {
          if (window.ntWishlist) {
            const w = window.ntWishlist;
            if (typeof w.count === 'function') return w.count();
            if (typeof w.count === 'number') return w.count;
            if (typeof w.handles === 'function') return w.handles().length;
            if (Array.isArray(w.handles)) return w.handles.length;
          }
          const raw = JSON.parse(localStorage.getItem('nt-wishlist') || '[]');
          if (Array.isArray(raw)) return raw.length;
          if (raw && Array.isArray(raw.handles)) return raw.handles.length;
          if (raw && typeof raw === 'object') return Object.keys(raw).length;
        } catch (e) { /* storage blocked */ }
        return 0;
      };
      const paint = (n) => counts.forEach((el) => { el.textContent = n > 99 ? '99+' : String(n); el.hidden = !(n > 0); });
      const onChange = (e) => {
        const d = e && e.detail;
        paint(d && typeof d.count === 'number' ? d.count : (d && Array.isArray(d.handles) ? d.handles.length : read()));
      };
      paint(read());
      document.addEventListener('nt:wishlist:change', onChange);
      window.addEventListener('nt:wishlist:change', onChange);
      window.addEventListener('storage', (e) => { if (e.key === 'nt-wishlist') paint(read()); });
    }
  }
  customElements.define('nt-header', NtHeader);
})();
